import { FC, useContext, useEffect, useMemo, useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Stomp } from '@stomp/stompjs'
import {
  createResponseContext,
  initialQueryResponse,
  QUERIES,
  getWebSocketUrl,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../../_metronic/helpers'
import { useQueryRequest } from './QueryRequestProvider'
import { useAuth } from '../../../../auth'
import { getNotificacoes } from '../../core/_requests'
import { NotificacaoList } from '../../core/_models'

const QueryResponseContext = createResponseContext<NotificacaoList>(initialQueryResponse)

export const QueryResponseProvider: FC<WithChildren> = ({ children }) => {
  const { state } = useQueryRequest()
  const { auth } = useAuth()
  const queryClient = useQueryClient()

  const query = useMemo(() => stringifyRequestQuery(state), [state])

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries([QUERIES.NOTIFICACOES_LIST])
  }, [queryClient])

  useEffect(() => {
    if (!auth?.accessToken) return

    const stompClient = Stomp.client(getWebSocketUrl('/socket/notificacoes'))

    stompClient.connect({}, () => {
      stompClient.subscribe('/topic/notificacoes', invalidateQuery)
    })

    return () => {
      stompClient.disconnect(() => {})
    }
  }, [auth?.accessToken, invalidateQuery])

  const {
    isFetching,
    isError,
    refetch,
    data: response,
  } = useQuery([QUERIES.NOTIFICACOES_LIST, query], () => getNotificacoes(query), {
    cacheTime: 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  return (
    <QueryResponseContext.Provider
      value={{ isLoading: isFetching, isError, refetch, response, query }}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

export const useQueryResponse = () => useContext(QueryResponseContext)
